import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';

/** Geschäftspartner-Art / -Status (CRM, ADR 0005). */
export const customerTypeSchema = z.enum(['CUSTOMER', 'PROSPECT', 'SUPPLIER']);
export type CustomerType = z.infer<typeof customerTypeSchema>;

export const customerStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export type CustomerStatus = z.infer<typeof customerStatusSchema>;

/** Listen-Query für Kunden: Standard-Pagination + Filter (Typ/Status/Land/Gesellschaft/Branche). */
export const customerListQuerySchema = listQuerySchema.extend({
  type: customerTypeSchema.optional(),
  status: customerStatusSchema.optional(),
  /** Land (Klartext, matcht countryName ODER ISO-Code). */
  country: z.string().trim().max(120).optional(),
  /** Betreuende Gesellschaft (Owner): UUID einer LegalEntity, oder `none` für „ohne Zuordnung". */
  ownerLegalEntityId: z.string().trim().max(64).optional(),
  /** Branche (exakter Wert aus der Facetten-Liste). */
  industry: z.string().trim().max(120).optional(),
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;

/** Ein Filter-Facettenwert mit Trefferanzahl (z. B. Land „Deutschland" · 312). */
export const customerFacetSchema = z.object({ value: z.string(), count: z.number().int() });
export type CustomerFacet = z.infer<typeof customerFacetSchema>;

/** Verfügbare Filterwerte für die Kundenliste (Länder & Branchen, mit Anzahl). */
export const customerFacetsSchema = z.object({
  countries: z.array(customerFacetSchema),
  industries: z.array(customerFacetSchema),
});
export type CustomerFacets = z.infer<typeof customerFacetsSchema>;

const shortText = (max: number) => z.string().trim().max(max).optional();

export const createCustomerSchema = z.object({
  name: z.string().trim().min(1).max(200),
  number: z.string().trim().max(32).optional(),
  type: customerTypeSchema.default('CUSTOMER'),
  status: customerStatusSchema.default('ACTIVE'),
  /** Interne Gesellschaft (ADR 0029): markiert diesen Stamm als eigene LegalEntity (Intercompany). */
  internalLegalEntityId: z.string().uuid().nullable().optional(),
  /** Betreuende Gesellschaft (Owner): Default-Gesellschaft für Belege an diesen Kunden. */
  ownerLegalEntityId: z.string().uuid().nullable().optional(),
  industry: shortText(120),
  email: z.string().trim().email().max(200).optional(),
  phone: shortText(50),
  website: shortText(200),
  vatId: shortText(50),
  street: shortText(200),
  postalCode: shortText(20),
  city: shortText(120),
  country: shortText(120),
  paymentTermsDays: z.number().int().min(0).max(365).optional(),
  language: shortText(10),
  notes: z.string().trim().max(5000).optional(),
  /** Adresswarntext: prominenter Hinweis, der auf Belegen zum Kunden erscheint. */
  warningText: z.string().trim().max(1000).optional(),
});
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

export const updateCustomerSchema = createCustomerSchema.partial();
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

export const customerResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  number: z.string().nullable(),
  name: z.string(),
  type: customerTypeSchema,
  status: customerStatusSchema,
  internalLegalEntityId: z.string().uuid().nullable(),
  ownerLegalEntityId: z.string().uuid().nullable(),
  industry: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  vatId: z.string().nullable(),
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  countryName: z.string().nullable(),
  matchcode: z.string().nullable(),
  fax: z.string().nullable(),
  paymentTermsDays: z.number().int().nullable(),
  language: z.string().nullable(),
  notes: z.string().nullable(),
  warningText: z.string().nullable(),
  /** Altsystem-Kennungen (SoftENGINE-Import): Steuergebiet / Adressart-Code. */
  taxRegionCode: z.number().int().nullable(),
  addressTypeCode: z.number().int().nullable(),
  /** Belegsperre: keine Belege an diesen Kunden. */
  isBillingBlocked: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomerResponse = z.infer<typeof customerResponseSchema>;
